/** Minimal, dependency-free semver comparison — enough for update checks (no ranges). */

/** True when `candidate` is a strictly higher semver precedence than `base`. */
export function isNewerVersion(candidate: string, base: string): boolean {
  return compareVersions(candidate, base) > 0
}

/** Returns -1, 0 or 1 following semver precedence (build metadata ignored, prerelease < release). */
export function compareVersions(a: string, b: string): number {
  const pa = parse(a)
  const pb = parse(b)

  for (let i = 0; i < 3; i++) {
    if (pa.main[i] !== pb.main[i]) return pa.main[i] < pb.main[i] ? -1 : 1
  }

  // A version WITHOUT a prerelease outranks one WITH a prerelease (1.0.0 > 1.0.0-rc.1).
  if (pa.pre.length === 0 && pb.pre.length === 0) return 0
  if (pa.pre.length === 0) return 1
  if (pb.pre.length === 0) return -1

  const len = Math.max(pa.pre.length, pb.pre.length)
  for (let i = 0; i < len; i++) {
    if (i >= pa.pre.length) return -1 // fewer identifiers = lower precedence
    if (i >= pb.pre.length) return 1
    const x = pa.pre[i]
    const y = pb.pre[i]
    const xNum = /^\d+$/.test(x)
    const yNum = /^\d+$/.test(y)
    if (xNum && yNum) {
      const nx = Number(x)
      const ny = Number(y)
      if (nx !== ny) return nx < ny ? -1 : 1
    } else if (xNum !== yNum) {
      return xNum ? -1 : 1 // numeric identifiers rank lower than alphanumeric
    } else if (x !== y) {
      return x < y ? -1 : 1
    }
  }
  return 0
}

function parse(version: string): { main: [number, number, number]; pre: string[] } {
  const cleaned = version.trim().replace(/^v/i, '').split('+', 1)[0] // drop build metadata
  const dash = cleaned.indexOf('-')
  const core = dash === -1 ? cleaned : cleaned.slice(0, dash)
  const pre = dash === -1 ? '' : cleaned.slice(dash + 1)
  const parts = core.split('.').map((n) => Number.parseInt(n, 10) || 0)
  return { main: [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0], pre: pre ? pre.split('.') : [] }
}
